import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import useUpdateConversationParticipant from "common/datahooks/useUpdateConversationParticipant";
import useUpdateDetailedLead from "common/datahooks/useUpdateDetailedLead";
import useUpdateLead from "common/datahooks/useUpdateLead";
import { del, patch, post } from "common/helpers/HTTP";
import type { LabelSchemaType } from "common/schemas";
import type { Label, Lead } from "common/types";

import type { LabelType } from "../types";

async function createLabelRequest(
  workspaceId: string,
  type: LabelType,
  label: LabelSchemaType,
): Promise<Label> {
  return (
    await post(
      `workspaces/${workspaceId}/${type === "workspace" ? "labels" : `${type}-labels`}`,
      label,
    )
  ).label;
}
async function deleteLabelRequest(
  workspaceId: string,
  type: LabelType,
  labelId: string,
) {
  await del(
    `workspaces/${workspaceId}/${type === "workspace" ? "labels" : `${type}-labels`}/${labelId}`,
  );
}
async function updateLabelRequest(
  workspaceId: string,
  type: LabelType,
  labelId: string,
  updatedLabel: LabelSchemaType,
) {
  await patch(
    `workspaces/${workspaceId}/${type === "workspace" ? "labels" : `${type}-labels`}/${labelId}`,
    updatedLabel,
  );
}

export default function useAllLabelsMutations(type: LabelType) {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { updateAllLeads } = useUpdateLead();
  const { updateAllDetailedLeads } = useUpdateDetailedLead();
  const { updateAllConversationParticipants } =
    useUpdateConversationParticipant();

  const { labelsKeys } = getQueryKeys(workspaceId);

  async function updateCachedLeads(
    updateFunction: (lead: Pick<Lead, "labels">) => void,
  ) {
    await updateAllLeads(updateFunction);
    await updateAllDetailedLeads(updateFunction);
    await updateAllConversationParticipants(updateFunction);
  }

  const { mutateAsync: createLabel, isPending: isCreatingLabel } = useMutation({
    mutationFn: (label: LabelSchemaType) =>
      createLabelRequest(workspaceId, type, label),
    onSuccess: (label) => {
      queryClient.setQueryData(labelsKeys.list(type), (prevLabels: Label[]) => [
        label,
        ...prevLabels,
      ]);
    },
    onError: (error) =>
      toast.error(
        "data" in error && error.data === "LabelNameTaken"
          ? "Label name already taken"
          : "Error adding label",
      ),
  });

  const { mutateAsync: deleteLabel, isPending: isDeletingLabel } = useMutation({
    mutationFn: ({ labelId }: { labelId: string }) =>
      deleteLabelRequest(workspaceId, type, labelId),
    onSuccess: async (_, { labelId }) => {
      queryClient.setQueryData(labelsKeys.list(type), (prevLabels: Label[]) =>
        prevLabels.filter(({ id }) => id !== labelId),
      );
      await updateCachedLeads((lead) => {
        lead.labels = lead.labels.filter(({ id }) => id !== labelId);
      });
    },
    onError: () => toast.error("Error deleting label"),
  });

  const { mutateAsync: updateLabel, isPending: isUpdatingLabel } = useMutation({
    mutationFn: ({
      labelId,
      label,
    }: {
      labelId: string;
      label: LabelSchemaType;
    }) => updateLabelRequest(workspaceId, type, labelId, label),
    onSuccess: async (_, { labelId, label }) => {
      queryClient.setQueryData(labelsKeys.list(type), (prevLabels: Label[]) =>
        produce(prevLabels, (draftState) => {
          for (let i = 0; i < draftState.length; i += 1) {
            if (draftState[i].id === labelId) {
              draftState[i] = { ...draftState[i], ...label };
            }
          }
        }),
      );
      await updateCachedLeads((lead) => {
        for (let i = 0; i < lead.labels.length; i += 1) {
          if (lead.labels[i].id === labelId) {
            lead.labels[i] = { ...lead.labels[i], ...label };
          }
        }
      });
    },
    onError: (error) =>
      toast.error(
        "data" in error && error.data === "LabelNameTaken"
          ? "Label name already taken"
          : "Error updating label",
      ),
  });
  return {
    createLabel,
    isCreatingLabel,
    deleteLabel,
    isDeletingLabel,
    updateLabel,
    isUpdatingLabel,
  };
}
